import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useToast } from '../../components/Toast/ToastContext';
import Modal, { ModalActions, ModalTitle } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput } from '../../components/FormField/PortalInput';
import { PortalCombobox } from '../../components/FormField/PortalCombobox';
import { assignDeviceMutation, rescuePetsQuery } from '../../lib/rescueQueries';
import type { RescuePetsData } from '../../lib/rescueQueries';
import PetFormModal from '../Dashboard/PetFormModal';
import styles from './Devices.module.scss';

interface AddDeviceToPetModalProps {
  open: boolean;
  onClose: () => void;
  moduleId: string | null;
}

type Mode = 'existing' | 'new';

export default function AddDeviceToPetModal({ open, onClose, moduleId }: AddDeviceToPetModalProps) {
  const { showToast } = useToast();
  const [mode, setMode] = useState<Mode>('existing');
  const [petId, setPetId] = useState('');
  const [error, setError] = useState('');

  const { data: petsData, loading: petsLoading } = useQuery<RescuePetsData>(rescuePetsQuery, {
    skip: !open,
  });

  const pets = useMemo(() => {
    if (!petsData) {
      return [];
    }
    const rescueHousehold = petsData.currentUser?.userHouseholds?.find((uh) => uh.household.type === 'RESCUE');
    const allPets = rescueHousehold?.household.pets ?? [];
    return allPets.filter((pet) => !pet.device).sort((a, b) => a.name.localeCompare(b.name));
  }, [petsData]);

  const petOptions = useMemo(
    () =>
      pets.map((pet) => ({
        value: pet.id,
        label: pet.breed?.name ? `${pet.name} \u2014 ${pet.breed.name}` : pet.name,
      })),
    [pets],
  );

  const [assignDevice, { loading: assigning }] = useMutation(assignDeviceMutation, {
    refetchQueries: 'active' as const,
  });

  function handleClose() {
    setMode('existing');
    setPetId('');
    setError('');
    onClose();
  }

  async function handleAssignExisting() {
    if (!moduleId || !petId) {
      return;
    }
    try {
      await assignDevice({ variables: { moduleId, petId } });
      const pet = pets.find((p) => p.id === petId);
      showToast(`Device ${moduleId} assigned to ${pet?.name ?? 'pet'}`);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign device');
    }
  }

  async function handleCreatedPet(pet: { id: string; name: string }) {
    if (!moduleId) {
      return;
    }
    try {
      await assignDevice({ variables: { moduleId, petId: pet.id } });
      showToast(`Device ${moduleId} assigned to ${pet.name}`);
      handleClose();
    } catch (err) {
      // Pet exists in DB; user can recover via the existing-pet flow.
      const detail = err instanceof Error ? err.message : 'Unknown error';
      showToast(
        `Pet "${pet.name}" was created but device assignment failed: ${detail}. Pick "${pet.name}" from the existing pet list to retry.`,
      );
      handleClose();
    }
  }

  if (!moduleId) {
    return null;
  }

  const hasPets = pets.length > 0;
  const canSubmitExisting = hasPets && petId.length > 0 && !assigning;

  return (
    <>
      <Modal open={open && mode === 'existing'} onClose={handleClose}>
        <ModalTitle>Add Device to Pet</ModalTitle>
        <p className={styles.modalDescription}>
          Assign device <strong>{moduleId}</strong> to one of your rescue&rsquo;s pets without a device.
        </p>

        <div className={styles.modeToggle}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setMode('new');
              setError('');
            }}
          >
            + Create new pet
          </Button>
        </div>

        <PortalField label="Pet">
          {petsLoading ? (
            <PortalInput value="" placeholder="Loading pets..." disabled readOnly />
          ) : !hasPets ? (
            <PortalInput value="" placeholder="No pets available" disabled readOnly />
          ) : (
            <PortalCombobox
              options={petOptions}
              value={petId}
              onChange={(val) => {
                setPetId(val);
                setError('');
              }}
              placeholder="Select from existing"
            />
          )}
        </PortalField>

        {error && (
          <div className={styles.modalError} role="alert">
            {error}
          </div>
        )}

        <ModalActions>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="green" onClick={handleAssignExisting} disabled={!canSubmitExisting}>
            {assigning ? 'Assigning...' : 'Assign Device'}
          </Button>
        </ModalActions>
      </Modal>

      <PetFormModal open={open && mode === 'new'} onClose={handleClose} mode="create" onCreated={handleCreatedPet} />
    </>
  );
}
